import {
  Cryoprotectantv4,
  CrystalDTO,
  CrystalFilesDTO,
  CrystalPuckDewarDTO,
  CrystalTripResultv4,
  DewarDTO,
  IPFDocProteinFormulationDTO,
  PFDocAttachmentDTO,
  PFDocProteinFormulationDTO,
  PFDocProteinFormulationIngredientsDTO,
  PFDocStandardIngredientsDTO,
  PuckDTO,
  Soakingv4,
  SynchTripDTO,
} from 'business/models/Models';
import { CrystalDataDTO, SynchTripDataDTO } from 'components/synchrotron/constants';
import {
  getDtoArrayFromPlainArray,
  getDtoFromPlainObject,
  getPlainArray,
  getPlainObject,
} from './utils';

export const getPlainCrystalData = (crystals: CrystalDTO[]): CrystalDataDTO[] => {
  if (!Array.isArray(crystals)) {
    return [];
  }
  return crystals.map((item) => {
    const {
      assignedPuckId,
      assignedDewarId,
      assignedPinPosition,
      proteinFormulationDTO,
      attachedFiles,
      result,
      cryoprotectants,
      soakings,
      puckDewar,
      ...rest
    } = getPlainObject(item);
    return {
      ...rest,
      proteinFormulationDTO: proteinFormulationDTO
        ? getPlainProteinFormulationData(proteinFormulationDTO)
        : null,
      attachedFiles: attachedFiles?.length ? getPlainArray(attachedFiles) : null,
      result: result ? getPlainObject(result) : null,
      cryoprotectants: cryoprotectants ? getPlainArray(cryoprotectants) : null,
      soakings: soakings ? getPlainArray(soakings) : null,
      puckDewar: puckDewar ? getPlainObject(puckDewar) : null,
      crystalContainerAssignments: {
        puckPinPosition: assignedPinPosition,
        puckId: assignedPuckId,
        dewarId: assignedDewarId,
        isDirty: false,
        isUpdated: false,
      },
    };
  });
};

export const getPlainProteinFormulationData = (
  proteinFormulationDto: PFDocProteinFormulationDTO,
) => {
  return getPlainObject(proteinFormulationDto);
};

export const getPlainSynchTripData = (synchTripData: SynchTripDTO): SynchTripDataDTO => {
  const { assignedCrystals, ...rest } = getPlainObject(synchTripData);
  return {
    ...rest,
    assignedCrystals: getPlainCrystalData(assignedCrystals),
    isCrystalAssignmentsDirty: false,
    editStatus: { isDirty: false, isSaving: false },
  };
};

export const getProteinFormulationDtoFromPlainData = (
  proteinData: IPFDocProteinFormulationDTO,
): PFDocProteinFormulationDTO => {
  const {
    datePrepared,
    proteinFormulationIngredient,
    proteinFormulationAttachments,
    proteinFormulationStandardIngredient,
    ...rest
  } = proteinData;
  const proteinDto = new PFDocProteinFormulationDTO({ ...rest, datePrepared });
  proteinDto.proteinFormulationAttachments = proteinFormulationAttachments?.map(
    (item) => new PFDocAttachmentDTO(item),
  );
  proteinDto.proteinFormulationIngredient = proteinFormulationIngredient?.map(
    (item) => new PFDocProteinFormulationIngredientsDTO(item),
  );
  proteinDto.proteinFormulationStandardIngredient =
    proteinFormulationStandardIngredient?.map(
      (item) => new PFDocStandardIngredientsDTO(item),
    );
  if (datePrepared) {
    proteinDto.datePrepared = new Date(datePrepared);
  }
  return proteinDto;
};

export const getCrystalDtoFromPlainData = (crystals: CrystalDataDTO[]): CrystalDTO[] => {
  return crystals.map((crystal) => {
    const {
      crystalContainerAssignments,
      proteinFormulationDTO,
      cryoprotectants,
      soakings,
      puckDewar,
      dateFrozen,
      dateCollected,
      attachedFiles,
      result,
      ...rest
    } = crystal;
    const crystalDto = new CrystalDTO({
      ...rest,
      dateFrozen: dateFrozen ? new Date(dateFrozen) : null,
      dateCollected: dateCollected ? new Date(dateCollected) : null,
    });
    crystalDto.proteinFormulationDTO = proteinFormulationDTO
      ? getProteinFormulationDtoFromPlainData(proteinFormulationDTO)
      : null;
    crystalDto.result ? getDtoFromPlainObject(result, CrystalTripResultv4) : null;
    crystalDto.attachedFiles?.length
      ? getDtoArrayFromPlainArray(attachedFiles, CrystalFilesDTO)
      : null;
    crystalDto.cryoprotectants
      ? getDtoArrayFromPlainArray(cryoprotectants, Cryoprotectantv4)
      : null;
    crystalDto.soakings ? getDtoArrayFromPlainArray(soakings, Soakingv4) : null;
    crystalDto.puckDewar ? getDtoFromPlainObject(puckDewar, CrystalPuckDewarDTO) : null;
    if (crystalContainerAssignments) {
      crystalDto.assignedPinPosition = crystalContainerAssignments.puckPinPosition;
      crystalDto.assignedPuckId = crystalContainerAssignments.puckId;
      crystalDto.assignedDewarId = crystalContainerAssignments.dewarId;
      crystalDto.isAssignmentsUpdated = crystalContainerAssignments.isUpdated;
    }

    return crystalDto;
  });
};

export const getSynchTripDtoFromPlainData = (
  synchTripData: SynchTripDataDTO,
): SynchTripDTO => {
  const { assignedCrystals, dataCollectionDate, ...rest } = synchTripData;
  const synchTripDto: SynchTripDTO = new SynchTripDTO({
    ...rest,
    dataCollectionDate: dataCollectionDate ? new Date(dataCollectionDate) : null,
  });
  synchTripDto.assignedCrystals = getCrystalDtoFromPlainData(assignedCrystals);
  return synchTripDto;
};

export const getPlainPucks = (pucks: PuckDTO[]) =>
  pucks.map((puck) => {
    const { puckType, ...rest } = getPlainObject(puck);
    return {
      ...rest,
      puckType: getPlainObject(puckType),
    };
  });
export const getPlainDewars = (dewars: DewarDTO[]) => getPlainArray(dewars);
